import { type IntlShape } from "react-intl";
import { z } from "zod";

import { containersFormSchema, containersSchema } from "./cube";

const orderByOptionSchema = z.enum(["percent-filled", "distance", "surface-area", "volume", ""]);
const namedBoxListFormOrderByOptionSchema = z.enum(["percent-filled", "distance", "surface-area", "volume"]);

export const namedBoxListSelectionOptionsSchema = z.object({
	cutoffTime: z.coerce.number<number>().min(0),
	maxCartons: z.coerce.number<number>().min(0),
	orderBy: orderByOptionSchema.nullable().optional(),
	shrinkwrap: z.boolean(),
	shrinkwrapMaxDim1: z.number().optional(),
	shrinkwrapMaxDim2: z.number().optional(),
	shrinkwrapMaxDim3: z.number().optional(),
	shrinkwrapMinDim1: z.number().optional(),
	shrinkwrapMinDim2: z.number().optional(),
	shrinkwrapMinDim3: z.number().optional(),
});

const namedBoxListFormSelectionOptionsSchema = namedBoxListSelectionOptionsSchema.extend({
	orderBy: namedBoxListFormOrderByOptionSchema,
});

export const namedBoxListContainerOptionsSchema = z.object({
	incrementDimension1By: z.coerce.number<number>().min(0),
	incrementDimension2By: z.coerce.number<number>().min(0),
	incrementDimension3By: z.coerce.number<number>().min(0),
	maximumDimension1: z.coerce.number<number>().min(0),
	maximumDimension2: z.coerce.number<number>().min(0),
	maximumDimension3: z.coerce.number<number>().min(0),
	maximumWeight: z.coerce.number<number>().min(0),
	minimumDimension1: z.coerce.number<number>().min(0),
	minimumDimension2: z.coerce.number<number>().min(0),
	minimumDimension3: z.coerce.number<number>().min(0),
});

export const namedBoxListConfigurationSchema = z
	.object({
		boxList: containersSchema.nullable(),
		containerOptions: namedBoxListContainerOptionsSchema.nullable(),
		filename: z.string().nullable().optional().default(null),
		isDefault: z.boolean(),
		limit: z.coerce.number<number>().min(0),
		maximumWeight: z.coerce.number<number>().min(0),
		name: z.string().nonempty(),
		selectionOptions: namedBoxListSelectionOptionsSchema,
	})
	.superRefine((configuration, context) => {
		if ((configuration.boxList === null) === (configuration.containerOptions === null)) {
			context.addIssue({
				code: "custom",
				message: "A box list must contain exactly one active configuration type",
			});
		}
	});

export const namedBoxListSchema = z
	.record(z.string().min(1), namedBoxListConfigurationSchema)
	.superRefine((configurations, context) => {
		const entries = Object.entries(configurations);
		const defaultCount = entries.filter(([, configuration]) => configuration.isDefault).length;

		if (entries.length === 0) {
			context.addIssue({
				code: "custom",
				message: "At least one box list is required",
			});
		}

		if (defaultCount !== 1) {
			context.addIssue({
				code: "custom",
				message: "Exactly one default box list is required",
			});
		}

		for (const [name, configuration] of entries) {
			if (name !== configuration.name) {
				context.addIssue({
					code: "custom",
					message: "Each dictionary key must match its box-list name",
					path: [name, "name"],
				});
			}
		}
	});

export const namedBoxListConfigurationResponseSchema = z.object({
	configurations: namedBoxListSchema,
});

const fixedDraftSchema = z.object({
	boxList: containersFormSchema,
	// TODO review if filename should be used or just use boxList name
	filename: z.string().nullable(),
});

const namedBoxListFormBaseSchema = z.object({
	isDefault: z.boolean(),
	isNew: z.boolean(),
	maximumWeight: z.coerce.number<number>().min(0),
	selectionOptions: namedBoxListFormSelectionOptionsSchema,
	id: z.string(),
});

export const getNamedBoxListFormValuesSchema = (intl: IntlShape) => {
	const nameSchema = z
		.string()
		.nonempty(intl.formatMessage({ id: "Name is required", defaultMessage: "Name is required" }));
	const namedBoxListFormSchema = z.discriminatedUnion("activeType", [
		namedBoxListFormBaseSchema.extend({
			activeType: z.literal("fixed"),
			dynamicDraft: namedBoxListContainerOptionsSchema.nullable(),
			fixedDraft: fixedDraftSchema,
			maxCartons: z.coerce.number<number>().min(1),
			name: nameSchema,
		}),
		namedBoxListFormBaseSchema.extend({
			activeType: z.literal("dynamic"),
			dynamicDraft: namedBoxListContainerOptionsSchema,
			fixedDraft: fixedDraftSchema.nullable(),
			maxCartons: z.coerce.number<number>().min(0),
			name: nameSchema,
		}),
	]);

	return z
		.object({
			lists: z.array(namedBoxListFormSchema).min(1),
		})
		.superRefine((formValues, context) => {
			const defaultCount = formValues.lists.filter((list) => list.isDefault).length;
			const names = new Set<string>();

			if (defaultCount !== 1) {
				context.addIssue({
					code: "custom",
					message: intl.formatMessage({
						id: "Exactly one default box list is required",
						defaultMessage: "Exactly one default box list is required",
					}),
					path: ["lists"],
				});
			}

			for (const [index, list] of formValues.lists.entries()) {
				if (names.has(list.name)) {
					context.addIssue({
						code: "custom",
						message: intl.formatMessage({
							id: "Unique name is required",
							defaultMessage: "Unique name is required",
						}),
						path: ["lists", index, "name"],
					});
				}

				names.add(list.name);

				if (list.activeType === "dynamic") {
					if (list.dynamicDraft.maximumDimension1 <= list.dynamicDraft.minimumDimension1) {
						context.addIssue({
							code: "custom",
							message: intl.formatMessage({
								id: "Must be greater than Minimum Length",
								defaultMessage: "Must be greater than Minimum Length",
							}),
							path: ["lists", index, "dynamicDraft", "maximumDimension1"],
						});
					}

					if (list.dynamicDraft.maximumDimension2 <= list.dynamicDraft.minimumDimension2) {
						context.addIssue({
							code: "custom",
							message: intl.formatMessage({
								id: "Must be greater than Minimum Width",
								defaultMessage: "Must be greater than Minimum Width",
							}),
							path: ["lists", index, "dynamicDraft", "maximumDimension2"],
						});
					}

					if (list.dynamicDraft.maximumDimension3 <= list.dynamicDraft.minimumDimension3) {
						context.addIssue({
							code: "custom",
							message: intl.formatMessage({
								id: "Must be greater than Minimum Height",
								defaultMessage: "Must be greater than Minimum Height",
							}),
							path: ["lists", index, "dynamicDraft", "maximumDimension3"],
						});
					}

					if (list.dynamicDraft.incrementDimension1By <= 0) {
						context.addIssue({
							code: "custom",
							message: intl.formatMessage({
								id: "Must be greater than zero",
								defaultMessage: "Must be greater than zero",
							}),
							path: ["lists", index, "dynamicDraft", "incrementDimension1By"],
						});
					}

					if (list.dynamicDraft.incrementDimension2By <= 0) {
						context.addIssue({
							code: "custom",
							message: intl.formatMessage({
								id: "Must be greater than zero",
								defaultMessage: "Must be greater than zero",
							}),
							path: ["lists", index, "dynamicDraft", "incrementDimension2By"],
						});
					}

					if (list.dynamicDraft.incrementDimension3By <= 0) {
						context.addIssue({
							code: "custom",
							message: intl.formatMessage({
								id: "Must be greater than zero",
								defaultMessage: "Must be greater than zero",
							}),
							path: ["lists", index, "dynamicDraft", "incrementDimension3By"],
						});
					}
				}

				if (!list.isNew) {
					continue;
				}

				if (list.activeType === "fixed" && list.fixedDraft.boxList.length === 0) {
					context.addIssue({
						code: "custom",
						message: intl.formatMessage({
							id: "New box lists must be configured before saving",
							defaultMessage: "New box lists must be configured before saving",
						}),
						path: ["lists", index, "fixedDraft", "boxList"],
					});
				}
			}
		});
};

export type NamedBoxListConfiguration = z.infer<typeof namedBoxListConfigurationSchema>;
export type NamedBoxListDictionary = z.infer<typeof namedBoxListSchema>;
export type NamedBoxListFormValues = z.infer<ReturnType<typeof getNamedBoxListFormValuesSchema>>;
